/* SPATIALx ROUTER v2 — client-side <main> swap so the broadcast never stops.

   WHY THIS EXISTS
   The station bar (assets/spatialx-radio.js) holds its Audio object in a
   module-scope closure. It is never inserted into the DOM, so it survives
   anything that does not unload the document — and dies the instant one
   does. Every internal link on this site is a full page load, so every
   click kills the music. This file removes that one cause: it intercepts
   same-origin link clicks, fetches the destination, and replaces the
   contents of <main id="MainContent"> in place. The document is never torn
   down, so the closure — and the audio — carry straight through.

   WHAT v2 REMOVED, AND WHY IT IS NOT COMING BACK
   v1 shipped two features that had nothing to do with keeping audio alive
   and everything to do with the three worst bugs found in review. Both are
   gone, deliberately, and both removals are load-bearing:

     1. PREFETCH / hover-warming. It fetched link targets on pointerover.
        That meant hovering a /discount/<code> link issued the GET that
        APPLIES the discount code — a cart mutation caused by moving a
        mouse. It also let a warmed payload skip every response guard the
        live path runs, fanned out unbounded (one sweep across the artists
        roster measured ~258 concurrent full-document GETs), and competed
        with the MP3 stream this router exists to protect. It bought
        milliseconds. It is not in this file in any form: no warm cache, no
        pointerover or touchstart listener, no TTL, no config key. If you
        are about to add "just a little" speculative fetching, read §3
        first and note that /discount is now explicitly denied precisely
        because a GET on this storefront can have side effects.

     2. syncCart(). See §14 — the whole section is now an explanation of
        why there is no code there.

   IT IS AN ENHANCEMENT, NOT A DEPENDENCY
   The site is a working multi-page app. This file is a layer on top of it
   and it is written so that removing it, disabling it, or breaking it
   returns the site to exactly the behaviour it has today:

     - Boot is gated. If the flag is off, or the browser is missing any API
       we need, or sessionStorage is unusable, or window.sxMotion has no
       mount/unmount, or we are inside the theme editor, the module
       publishes { enabled: false } and returns having registered zero
       listeners and mutated zero globals. Not "mostly inert" — literally
       inert, including history.scrollRestoration, which is left alone.
     - Every declining path is a *return*, not a workaround. A link we do
       not want to route is simply not intercepted, and the browser does
       what it always did.
     - Almost every failing path ends in location.assign(). Fetch failed,
       timed out, came back as JSON, redirected to checkout, or the commit
       threw halfway — all of them terminate in a real navigation to the
       URL the user asked for. location.assign, never location.replace, so
       a pushState that already happened stays in history and Back works.
       The user cannot be stranded. Audio dies on that fallback, and that
       is fine: spatialx-radio.js persists {entry,time,playing} to
       sessionStorage and resumes on the far side.
       The ONE exception is a network-class failure while the browser
       reports itself offline (§18): there is no page to hand the user, so
       we keep the working one — and the audio — and say so. Handing a
       known-dead request to the browser only buys an error page.
     - Two consecutive failures trip a circuit breaker that turns the
       router off for the rest of that session. The counter lives in
       sessionStorage, because the statement immediately after incrementing
       it destroys the document (v1 kept it in a module variable, so it
       could never reach 2 and the breaker never tripped). A successful
       commit clears it, so two unrelated flakes an hour apart do not
       disable a healthy router.

   WHAT IS AND IS NOT TOUCHED
   Swapped:      the *contents* of <main id="MainContent">.
   Never touched: header-group (which contains #sx-radio), footer-group,
                  <cart-drawer>, #cart-icon-bubble, #sx-now-playing,
                  .sx-progress, .sx-roll, and <head> except for the
                  specific tags listed in syncHead() below. The <main>
                  element itself is kept and only its innerHTML is written,
                  so #MainContent, its tabindex="-1" and its role="main"
                  persist — which is what keeps the skip link pointing at
                  something real.

   LIFECYCLE
   We do not invent a teardown protocol. This theme already has one, in
   five separate places, and it is Shopify's: shopify:section:unload and
   shopify:section:load, dispatched on the section wrapper, bubbling, with
   detail.sectionId. spatialx-motion.js, spatialx-home-cinema.js, the home
   hero, the artists index and spatialx-radio.js all listen for exactly
   that shape. So the router fires those events itself, per section, and
   every existing teardown runs unmodified. sxMotion.unmount/mount are
   called explicitly on top of that because they are the documented
   contract and because mount() is idempotent.

   Order matters and is not obvious: scripts must be revived BEFORE
   shopify:section:load is dispatched, because the handler a section
   listens with is registered by the very script we are reviving. Fire
   first and it lands on nobody.

   SCRIPTS
   <script> inserted via innerHTML never executes. That is not a corner
   case here: /pages/artists builds its entire roster from an inline
   script, and the product page's add-to-cart interceptor is inline — lose
   it and the buy form falls back to a native POST to /cart/add, which is a
   full page load, which kills the audio. The router therefore rebuilds
   each script node (a freshly created element has its "already started"
   flag clear, so insertion always runs it) and re-inserts it in place.
   application/json and application/ld+json are left alone — the two data
   islands on this site are 90 KB and 172 KB and must stay inert.

   FLAG
   See enabled(). The intended production kill switch is the theme setting
   that renders <html data-sx-router="on">: unticking it in the customizer
   and saving turns the router off site-wide with no file push. The
   per-visitor kill is ?sx_router=0, which is read straight off the URL
   into a module variable — it does NOT depend on sessionStorage, because
   the visitors most likely to be handed that URL on a support call are
   exactly the ones whose storage throws.

   This file injects every stylesheet rule it needs (§5). Nothing here
   depends on a manual edit to a theme stylesheet. */
(function () {
  'use strict';

  var root = window;
  var doc = document;
  var VERSION = '2.0.0';

  /* ------------------------------------------------------------------ *
   * 0. CAPABILITY GATE
   *
   * Anything missing here and we are not routing. This is checked before
   * the flag because a browser that cannot do the job should not even be
   * asked. All of these are 2017-era; the check exists so that an old
   * Safari, a locked-down webview or a stripped-down bot gets the plain
   * MPA rather than a half-working SPA.
   * ------------------------------------------------------------------ */
  var CAPABLE = !!(
    root.fetch &&
    root.Promise &&
    root.AbortController &&
    root.DOMParser &&
    root.URL &&
    root.CustomEvent &&
    root.history && root.history.pushState &&
    doc.documentElement.classList &&
    Element.prototype.closest &&
    Element.prototype.querySelector
  );

  /* ------------------------------------------------------------------ *
   * 1. SESSION STORAGE
   *
   * Wrapped because Safari private mode throws on setItem, and a storage
   * exception must never be the reason a click does nothing.
   *
   * Two things are new in v2 and both matter:
   *
   *   - Probe once, honestly. Chrome throws SecurityError on the
   *     sessionStorage GETTER when cookies are blocked for the site, and
   *     Brave/uBlock shields, enterprise policy, embedded webviews and
   *     quota exhaustion all produce the same shape. STORAGE_OK is a real
   *     write-read-delete round trip, not a typeof check.
   *   - Mirror every write in memory. sget/sset/sdel keep working for the
   *     lifetime of the document even when storage is dead, which is what
   *     makes sxRouter.disable() and the ?sx_router=0 pin behave inside
   *     this document rather than silently evaporating.
   *
   * Storage failure then resolves to OFF, not ON (see enabled()). Without
   * a working store we cannot keep the per-entry scroll counter honest
   * (every history entry would take the id 1 and share one offset slot)
   * and we cannot remember a tripped breaker. Declining to route is the
   * conservative answer, and it is the answer the escape hatch needs.
   * ------------------------------------------------------------------ */
  var memStore = {};

  var STORAGE_OK = (function () {
    try {
      var probe = '__sx_router_probe__';
      sessionStorage.setItem(probe, '1');
      var ok = sessionStorage.getItem(probe) === '1';
      sessionStorage.removeItem(probe);
      return ok;
    } catch (e) { return false; }
  }());

  function sget(key) {
    if (STORAGE_OK) {
      try { return sessionStorage.getItem(key); } catch (e) {}
    }
    return Object.prototype.hasOwnProperty.call(memStore, key) ? memStore[key] : null;
  }
  function sset(key, value) {
    memStore[key] = value;
    if (STORAGE_OK) {
      try { sessionStorage.setItem(key, value); } catch (e) {}
    }
  }
  function sdel(key) {
    delete memStore[key];
    if (STORAGE_OK) {
      try { sessionStorage.removeItem(key); } catch (e) {}
    }
  }

  var K_FLAG = 'sx-router';           // 'on' | 'off', set by ?sx_router=
  var K_BREAK = 'sx-router-breaker';  // 'off' once tripped
  var K_FAILS = 'sx-router-fails';    // consecutive hard fallbacks this session
  var K_SEQ = 'sx-router-seq';        // monotonic history-entry counter
  var K_SCROLL = 'sx-router-scroll';  // { sxId: yOffset }
  var K_SKIP = 'sx-router-skip';      // comma-separated template denylist

  /* Read ?sx_router= once, at boot, into a MODULE VARIABLE. v1 wrote it
     only to sessionStorage and read it back through sget(), so for any
     visitor whose storage throws the documented kill switch did nothing at
     all — not even for the page load it appeared on. QUERY_FLAG is
     authoritative for this document no matter what storage does; the
     sset() below is a best-effort pin for the rest of the session.
     ?sx_router=1 also clears a tripped breaker and its failure count,
     which is how you re-arm the router after it has given up on you. */
  var QUERY_FLAG = null;
  (function readQueryFlag() {
    if (!CAPABLE) return;
    try {
      var qs = new URL(root.location.href).searchParams;
      if (!qs || !qs.has('sx_router')) return;
      QUERY_FLAG = qs.get('sx_router') !== '0';
      sset(K_FLAG, QUERY_FLAG ? 'on' : 'off');
      if (QUERY_FLAG) { sdel(K_BREAK); sdel(K_FAILS); }
    } catch (e) {}
  }());

  /* ------------------------------------------------------------------ *
   * 2. THE FLAG
   *
   * Re-evaluated on every click. No cached boolean anywhere. Off beats on
   * in every case, and the theme editor beats everything.
   * ------------------------------------------------------------------ */
  function enabled() {
    if (!CAPABLE) return false;

    /* --- hard offs, in priority order --- */

    // The customizer re-renders sections through its own lifecycle and
    // would fight us for control of the DOM. Both checks, because the
    // layout emits the class from a separate script and either one could
    // in principle be stubbed.
    if (root.Shopify && root.Shopify.designMode) return false;
    if (doc.documentElement.classList.contains('shopify-design-mode')) return false;

    // ?sx_router=0 — read from the URL, not from storage. First of the
    // offs on purpose: this is the one a support call hands to a visitor
    // whose browser is already misbehaving, and it must win outright.
    if (QUERY_FLAG === false) return false;

    if (root.__sxRouterDead === true) return false;      // set by us, on a fatal
    if (root.SX_ROUTER === false) return false;          // set by anything, from anywhere
    if (doc.documentElement.hasAttribute('data-sx-router-off')) return false;

    // No usable session store — see §1. Ambiguity resolves to OFF.
    if (!STORAGE_OK) return false;

    if (sget(K_BREAK) === 'off') return false;           // circuit breaker

    var pinned = sget(K_FLAG);
    if (pinned === 'off') return false;                  // ?sx_router=0, this visitor

    /* --- ons --- */
    if (root.SX_ROUTER === true) return true;
    if (QUERY_FLAG === true) return true;                // ?sx_router=1, this document
    if (pinned === 'on') return true;                    // ?sx_router=1, this visitor
    return doc.documentElement.getAttribute('data-sx-router') === 'on';
  }

  /* If we are not routing, say so and touch nothing else. This early
     return is the single most important line in the file: it is what
     makes "ship it disabled" a genuine no-op rather than a promise. No
     listeners, no globals beyond this stub, and — note — no change to
     history.scrollRestoration. */
  if (!enabled()) {
    root.sxRouter = {
      enabled: false,
      version: VERSION,
      reason: !CAPABLE ? 'unsupported browser' : (!STORAGE_OK ? 'no sessionStorage' : 'flag off')
    };
    return;
  }

  /* ------------------------------------------------------------------ *
   * 3. CONSTANTS, STATE AND SMALL UTILITIES
   * ------------------------------------------------------------------ */
  var MAIN_ID = 'MainContent';
  var TIMEOUT_MS = 8000;      // hard ceiling on a fetch before we give up and hard-nav
  var SCRIPT_MS = 5000;       // hard ceiling on ONE revived src= script
  var LOADING_MS = 700;       // announce "Loading" to screen readers after this long
  var TOAST_MS = 3600;        // how long the visible "could not load" pill stays up
  var SCROLL_KEEP = 60;       // history entries whose offset we remember
  var BREAK_AT = 2;           // consecutive hard fallbacks before we switch off
  var MAX_SWAPS = 60;         // swaps per document before we hand one back on purpose
  var PRELOAD_MAX = 16;       // asset hints emitted per commit (see §11)

  /* Operator knobs. Set window.SX_ROUTER_OPTIONS before this file runs.
     Everything here has a safe default and none of it is required. */
  var OPT = root.SX_ROUTER_OPTIONS || {};

  /* Assigned in boot(), not here. This file is meant to be loaded with
     defer from <head>, but it must not quietly disable itself if someone
     drops the tag in without it — nothing about a script tag's attributes
     should decide whether the site has a router. Everything that needs
     the DOM is therefore deferred to boot(), which runs either
     immediately (document already parsed) or on DOMContentLoaded. */
  var main = null;

  var seq = 0;                     // monotonic navigation token
  var inflight = null;             // { controller, token, url, popped }
  var rendered = new URL(root.location.href);
  var swaps = 0;                   // successful commits in this document
  var announceTimer = null;
  var loadingTimer = null;
  var toastTimer = null;
  var toast = null;

  function slice(list) { return Array.prototype.slice.call(list || []); }

  function each(list, fn) {
    var arr = slice(list);
    for (var i = 0; i < arr.length; i++) fn(arr[i], i);
  }

  /* Counters and a bounded log ring. A router that falls back silently
     and disables itself after two failures is undiagnosable without
     these: the first support call gets answered from
     sxRouter.state() in the console instead of from a repro. */
  var STATS = {
    intercepted: 0,
    declined: 0,
    committed: 0,
    hardNavigations: 0,
    stayedPut: 0,
    errors: 0
  };

  var LOG_KEEP = 40;
  var LOG = [];

  /* Never store the object itself. `extra` is routinely an Element or an
     Error, and a ring buffer holding an element from a swapped-out <main>
     would pin that entire detached subtree for the life of the document —
     the exact leak class this router is trying not to add. */
  function brief(x) {
    if (x === null || x === undefined) return '';
    if (typeof x === 'string') return x.length > 200 ? x.slice(0, 200) : x;
    if (x.message) return String(x.message);
    try { return String(x); } catch (e) { return '?'; }
  }

  function log(msg, extra) {
    LOG.push({ t: Date.now(), msg: msg, extra: brief(extra) });
    while (LOG.length > LOG_KEEP) LOG.shift();
    if (!root.__sxRouterDebug) return;
    try { (root.console && console.info || function () {}).call(console, '[sx-router] ' + msg, extra); } catch (e) {}
  }

  /* Every third-party surface we poke is optional and some of it is
     minified Dawn we do not control. Wrapping the call is cheaper than
     feature-detecting each one, and a throw inside a teardown must never
     abort the navigation that owns it. */
  function attempt(fn) {
    try { return fn(); } catch (e) { log('suppressed', e); return undefined; }
  }

  /* The circuit-breaker counter. It has to survive the document, because
     the statement right after we increment it is location.assign() — v1
     kept it in a module variable, so it reset to 0 on the very next line's
     page load and could only ever reach 1. BREAK_AT is 2, so the breaker
     was unreachable in practice and sxRouter.state().failures, which the
     handover tells the operator to watch, could never report the
     condition it was written for. */
  function failureCount() {
    return parseInt(sget(K_FAILS) || '0', 10) || 0;
  }
  function bumpFailures() {
    var n = failureCount() + 1;
    sset(K_FAILS, String(n));
    return n;
  }
  function clearFailures() {
    if (failureCount()) sdel(K_FAILS);
  }

  /* ------------------------------------------------------------------ *
   * 4. WHAT WE ARE ALLOWED TO ROUTE
   *
   * This is a denylist of paths the browser must own, not a permit list
   * of paths we like. Anything unknown is routed, because the fetch
   * guards downstream (final URL, status, content-type, presence of
   * <main>) will catch a surprise and fall back safely. But these must
   * never even be attempted — a fetch to /checkout follows a redirect to
   * shop.app, which has no access-control-allow-origin, and rejects with
   * a CORS TypeError after the user has already waited.
   *
   * /discount is the one that is easy to get wrong and expensive to get
   * wrong. Verified against the live storefront:
   *
   *     $ curl -sI https://spatialx.media/discount/HELLO
   *     HTTP/2 302
   *     location: https://spatialx.media/
   *     set-cookie: discount_code=HELLO; path=/; SameSite=Lax
   *     set-cookie: cart=hWNFoKz9fX7jFeUn6y5JUga3?key=...; path=/
   *
   * A GET that mutates the cart, whose 302 target is a perfectly routable
   * page. There is no /discount link in the theme today, but a merchant
   * drops one into a homepage banner or a blog post without a deploy, and
   * a router that fetches it applies it. Denied here as defence in depth;
   * the actual fix for the class of bug was deleting prefetch, so that no
   * request is ever issued for a link the user has not clicked.
   * ------------------------------------------------------------------ */
  var DENY = [
    '/cart',                    // and /cart/add|change|update|clear
    '/discount', '/discounts',  // GET /discount/<code> sets discount_code + cart cookies
    '/checkout', '/checkouts', '/orders', '/gift_cards', '/payments',
    '/digital_wallets', '/wallets',
    '/account', '/customer_authentication', '/customer_identity',
    '/apps', '/a', '/tools', '/services', '/community',   // reserved app-proxy prefixes
    '/cdn', '/wpm', '/.well-known',
    '/password', '/localization', '/challenge', '/throttle',
    '/search/suggest', '/recommendations'                 // Dawn's own JSON partials
  ];

  // Things that are plainly not documents. /products.json, /collections/all.atom
  // and /sitemap.xml are all live Shopify routes on this shop.
  var NOT_A_DOCUMENT = /\.(json|js|mjs|css|xml|atom|rss|txt|csv|pdf|zip|gz|mp3|mp4|m4a|wav|flac|webm|mov|png|jpe?g|gif|webp|avif|svg|ico|woff2?|ttf|otf)$/i;

  // Params that mean "this response is not a page": the Section Rendering
  // API returns a 200 with no <main> at all, and Shopify's own redirect
  // and handshake params trigger a 302 + cookie exchange we must let the
  // browser do. _bt and key are the two that carry signed tokens.
  var HIJACK_PARAMS = ['section_id', 'sections', '_ab', '_fd', '_sc', '_bt', 'key', 'preview_theme_id'];

  /* Shopify's consent banner reads this fragment at DOCUMENT LOAD. A
     pushState fires no hashchange and there is no reload, so routing the
     footer's "Cookie preferences" link would leave the banner unopenable
     with no error anywhere. Cheaper to decline the link than to explain
     it later. */
  var CONSENT_HASH = 'shopifyreshowconsentbanner';

  /* The shop is single-locale today (Shopify.routes.root === '/') but a
     market prefix would silently break every denylist entry, so strip it
     before matching rather than after someone adds French. */
  function shopRoot() {
    var r = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function localPath(url) {
    var r = shopRoot();
    var p = url.pathname;
    return (r !== '/' && p.indexOf(r) === 0) ? '/' + p.slice(r.length) : p;
  }

  function isRoutableURL(url) {
    if (url.origin !== root.location.origin) return false;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;

    var p = localPath(url);
    for (var i = 0; i < DENY.length; i++) {
      // Prefix-with-slash, so /cartoons is not caught by /cart.
      if (p === DENY[i] || p.indexOf(DENY[i] + '/') === 0) return false;
    }
    if (NOT_A_DOCUMENT.test(p)) return false;

    var qs = url.searchParams;
    if (qs) {
      for (var j = 0; j < HIJACK_PARAMS.length; j++) {
        if (qs.has(HIJACK_PARAMS[j])) return false;
      }
    }
    return true;
  }

  /* ------------------------------------------------------------------ *
   * 4b. TEMPLATE ESCAPE HATCH
   *
   * The ten-second lever for an incident. A per-link data-no-router is a
   * markup hunt; this is one config line, it works no matter which link
   * was clicked, and it catches /pages/* with custom suffixes whose URL
   * does not reveal the template. Read from the FETCHED document, so the
   * decision is made on what the server actually returned.
   *
   * Three sources, all optional, all empty by default — this is a lever,
   * not a policy:
   *     window.SX_ROUTER_OPTIONS = { hardTemplates: ['collection'] }
   *     <html data-sx-router-skip="collection,search">
   *     sxRouter.skipTemplates('collection')     // console, survives reload
   *
   * Critically the decline is a NON-COUNTING hand-back. Routing it through
   * the failure path would let two clicks on an opted-out template trip
   * the circuit breaker and disable the router wholesale — an escape hatch
   * that breaks the thing it was pulled to protect.
   * ------------------------------------------------------------------ */
  function splitList(s) {
    var out = [];
    each(String(s || '').split(','), function (part) {
      var v = part.replace(/^\s+|\s+$/g, '');
      if (v) out.push(v);
    });
    return out;
  }

  function skipTemplates() {
    var list = [];
    if (OPT.hardTemplates && OPT.hardTemplates.length) list = list.concat(slice(OPT.hardTemplates));
    list = list.concat(splitList(doc.documentElement.getAttribute('data-sx-router-skip')));
    list = list.concat(splitList(sget(K_SKIP)));
    return list;
  }

  function templateOptedOut(name) {
    if (!name) return false;
    var list = skipTemplates();
    for (var i = 0; i < list.length; i++) {
      if (list[i] === name) return true;
    }
    return false;
  }

  /* ------------------------------------------------------------------ *
   * 5. FEEDBACK: ANNOUNCER, PROGRESS, TOAST
   *
   * The announcer is built empty, at boot, outside <main>, before any
   * navigation can happen. Screen readers build their live-region model
   * when the node enters the accessibility tree; a region created and
   * filled in the same task is announced unreliably or not at all. The
   * existing .sxc-announcer lives inside <main> and is destroyed by every
   * swap, so it cannot be reused.
   *
   * role="status" (implicit aria-live="polite") satisfies SC 4.1.3 Status
   * Messages. Deliberately not assertive: a route change is not an error
   * and must not interrupt whatever the user is mid-sentence on.
   *
   * v1 documented two CSS rules it "expects" a stylesheet to carry and
   * then injected neither, so sighted users got no signal at all for the
   * whole fetch — up to 8 seconds of a page that looked like it had
   * ignored the click. Everything this file needs it now injects itself,
   * driving the theme's existing .sx-progress hairline (a fixed 2px bar
   * at the top of <body>, outside <main>, so it survives every swap).
   * ------------------------------------------------------------------ */
  var STR = root.accessibilityStrings || {};
  var T_NAV = STR.routerNavigated || 'Navigated to [title]';
  var T_LOAD = STR.routerLoading || 'Loading';
  var T_OFFLINE = STR.routerOffline || 'You appear to be offline. That page was not loaded.';

  var announcer = null;

  function buildAnnouncer() {
    var n = doc.getElementById('sx-router-announcer');
    if (n) return n;
    n = doc.createElement('p');
    n.id = 'sx-router-announcer';
    n.className = 'visually-hidden';
    n.setAttribute('role', 'status');
    n.setAttribute('aria-live', 'polite');
    n.setAttribute('aria-atomic', 'true');
    var skip = doc.querySelector('.skip-to-content-link');
    if (skip && skip.parentNode) skip.parentNode.insertBefore(n, skip.nextSibling);
    else doc.body.insertBefore(n, doc.body.firstChild);
    return n;
  }

  /* Assert the hiding technique rather than trusting the theme's
     .visually-hidden to stay clip-based. A display:none live region is
     not announced at all, and that failure is completely silent.

     The .sx-progress rules are the sighted equivalent of the announcer.
     The theme drives that bar from --sx-scroll; while a navigation is
     pending we take it over with an 8s ease that never quite completes —
     8s is TIMEOUT_MS, so the bar cannot claim to be finished before we
     have either committed or handed the click to the browser. Under
     prefers-reduced-motion the theme already sets animation:none on it,
     and we match that with a static dimmed state rather than fighting it.

     The toast is for the one path where we deliberately do NOT navigate
     (offline, §18). A screen-reader-only message there would leave a
     sighted user staring at a link that did nothing. */
  function injectStyles() {
    var s = doc.createElement('style');
    s.setAttribute('data-sx-router', 'style');
    s.textContent =
      '#sx-router-announcer{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;' +
      'border:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap}' +
      'html.sx-router-swapping{overflow-anchor:none}' +
      'html.sx-nav-pending{cursor:progress}' +
      'html.sx-nav-pending .sx-progress{opacity:.85;animation:sx-router-progress 8s cubic-bezier(.2,.8,.2,1) forwards}' +
      '@keyframes sx-router-progress{from{transform:scaleX(.03)}to{transform:scaleX(.92)}}' +
      '#sx-router-toast{position:fixed;left:50%;bottom:calc(env(safe-area-inset-bottom, 0px) + 84px);' +
      'transform:translateX(-50%) translateY(10px);max-width:min(90vw,32rem);padding:.6rem 1.1rem;' +
      'border-radius:999px;font:500 .8rem/1.25 var(--sx-font-body, inherit);letter-spacing:.02em;' +
      'text-align:center;background:rgba(10,8,16,.94);color:#fff;border:1px solid rgba(255,255,255,.18);' +
      'box-shadow:0 10px 34px rgba(0,0,0,.45);z-index:400;pointer-events:none;opacity:0;' +
      'transition:opacity .18s ease, transform .18s ease}' +
      '#sx-router-toast.is-on{opacity:1;transform:translateX(-50%) translateY(0)}' +
      '@media (prefers-reduced-motion:reduce){html.sx-nav-pending .sx-progress{animation:none;opacity:.55}' +
      '#sx-router-toast{transition:none}}';
    doc.head.appendChild(s);
  }

  function buildToast() {
    var n = doc.createElement('div');
    n.id = 'sx-router-toast';
    // aria-hidden: the announcer above is the accessible channel, and a
    // second live region would double every message.
    n.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(n);
    return n;
  }

  function showToast(text) {
    if (!toast) return;
    clearTimeout(toastTimer);
    toast.textContent = text;
    toast.classList.add('is-on');
    toastTimer = setTimeout(function () { toast.classList.remove('is-on'); }, TOAST_MS);
  }

  function announce(text) {
    if (!announcer) return;
    clearTimeout(announceTimer);
    // Clear first: an aria-atomic region does not re-announce an unchanged
    // string, and two pages can legitimately share a title. The 120ms gap
    // also lets the "main region" utterance from focus() finish, so the
    // two queue instead of clobbering each other.
    announcer.textContent = '';
    announceTimer = setTimeout(function () { announcer.textContent = text; }, 120);
  }

  /* ------------------------------------------------------------------ *
   * 6. SCROLL: PER-HISTORY-ENTRY OFFSETS
   *
   * Keyed on a monotonic id stamped into history.state, not on the URL.
   * Back → forward → same URL can legitimately carry different offsets,
   * and a URL-keyed store gets those wrong every time.
   *
   * The counter lives in sessionStorage rather than a module variable
   * because this site has two buttons (#sx-roll-btn, the radio ticker)
   * that assign window.location.href directly. A real page load in the
   * middle of a session would reset an in-memory counter to zero, and the
   * new ids would collide with ids already sitting in the back stack.
   * (This is also why the router refuses to arm without a working store —
   * see §1. With storage dead every entry takes the id 1 and every Back
   * restores whatever position was recorded last, on any page.)
   *
   * currentEntryId, NOT history.state, is what recordScroll() writes to.
   * That is not a style preference: on a popstate the browser has ALREADY
   * flipped history.state to the DESTINATION entry, so a state-derived key
   * writes the outgoing page's scrollY into the slot we are about to
   * restore from. It self-heals only when the programmatic scroll happens
   * to fire a scroll event with a different value — not in a throttled or
   * hidden tab, and not when the restore target equals the outgoing Y.
   * ------------------------------------------------------------------ */
  var currentEntryId = null;

  var offsets = (function () {
    try { return JSON.parse(sget(K_SCROLL)) || {}; } catch (e) { return {}; }
  }());

  function persistOffsets() {
    var keys = Object.keys(offsets);
    if (keys.length > SCROLL_KEEP) {
      keys.sort(function (a, b) { return a - b; })
        .slice(0, keys.length - SCROLL_KEEP)
        .forEach(function (k) { delete offsets[k]; });
    }
    try { sset(K_SCROLL, JSON.stringify(offsets)); } catch (e) {}
  }

  function nextId() {
    var n = parseInt(sget(K_SEQ) || '0', 10) || 0;
    n += 1;
    sset(K_SEQ, String(n));
    return n;
  }

  /* Preserve whatever is already in history.state — Shopify apps and
     Dawn's own facets write to it — and add ours alongside. searchParams
     is Dawn's key, mirrored here so facets.js's popstate handler always
     sees a string instead of undefined. See the facets note in §15. */
  function stampState(replace, url) {
    var prev = history.state || {};
    var next = {};
    for (var k in prev) if (Object.prototype.hasOwnProperty.call(prev, k)) next[k] = prev[k];
    var id = (replace && typeof prev.sxId === 'number') ? prev.sxId : nextId();
    next.sxId = id;
    next.sxRouter = true;
    next.searchParams = (url || new URL(root.location.href)).search.slice(1);
    try {
      if (replace) history.replaceState(next, '', url ? url.href : root.location.href);
      else history.pushState(next, '', url.href);
    } catch (e) {}
    currentEntryId = id;
    return id;
  }

  function ensureEntryId() {
    var st = history.state;
    if (st && typeof st.sxId === 'number') { currentEntryId = st.sxId; return st.sxId; }
    return stampState(true, new URL(root.location.href));
  }

  function currentY() {
    return root.scrollY || doc.documentElement.scrollTop || 0;
  }

  function recordScroll() {
    if (typeof currentEntryId === 'number') offsets[currentEntryId] = currentY();
  }

  /* Read window.scrollY, never lenis.scroll. Lenis's `scroll` getter
     returns animatedScroll, which lags the real position mid-lerp — you
     would record a position the user is not actually at. */
  var scrollTick = false;

  function installScrollHandling() {
    root.addEventListener('scroll', function () {
      if (scrollTick) return;
      scrollTick = true;
      requestAnimationFrame(function () {
        scrollTick = false;
        recordScroll();
      });
    }, { passive: true });

    root.addEventListener('pagehide', persistOffsets);
    doc.addEventListener('visibilitychange', function () {
      if (doc.visibilityState === 'hidden') persistOffsets();
    });

    /* Taking manual control also disables restoration on a plain reload,
       so we have to do that job too — otherwise F5 silently starts losing
       the user's place, which is a regression the router did not need to
       cause. Note this is set only once the router is genuinely armed:
       with the flag off, or with no session store to key entries against,
       scrollRestoration is left at 'auto' and the browser keeps the job. */
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

    root.addEventListener('pageshow', function (e) {
      if (e.persisted) return;   // bfcache restores full rendering state itself; don't fight it
      var st = history.state;
      if (st && typeof st.sxId === 'number') {
        currentEntryId = st.sxId;
        if (typeof offsets[st.sxId] === 'number') restoreScroll(offsets[st.sxId], seq);
      }
    });
  }

  /* The fixed radio bar sits over the top of the document at z-index 390,
     and the sticky header sits under it. Measure rather than parse
     --sx-radio-h: the token changes at the mobile breakpoint, and the
     header's height is not in a token at all. */
  function barOffset() {
    var h = 0;
    var bar = doc.getElementById('sx-radio');
    if (bar) h += bar.getBoundingClientRect().height;
    var sticky = doc.querySelector('.shopify-section-header-sticky');
    if (sticky) h += sticky.getBoundingClientRect().height;
    return h + 12;
  }

  /* quiet=true suppresses the synthetic resize. The retry loop below can
     run twelve times in 200ms and a synthetic resize on every pass would
     hammer every resize listener on the page — including Lenis's own and
     the forge section's — for no benefit. One notification per navigation
     is what the .sx-progress hairline actually needs. */
  function applyScroll(y, quiet) {
    var max = Math.max(0, doc.documentElement.scrollHeight - root.innerHeight);
    var target = Math.min(Math.max(0, y), max);

    // Native first: the browser's clamp is authoritative because it knows
    // the real document height at this instant and we are guessing.
    try { root.scrollTo(0, target); } catch (e) { doc.documentElement.scrollTop = target; }

    var l = root.sxLenis;
    if (l) {
      // resize() is Lenis's "the world changed" primitive — it recomputes
      // dimensions (hence `limit`) AND re-syncs animatedScroll/targetScroll
      // to actualScroll in one step. Its own ResizeObserver is debounced
      // 250ms, which is far too slow for a swap: for those 250ms `limit` is
      // stale and any scroll toward the new bottom gets clamped short.
      attempt(function () { l.resize(); });
      attempt(function () { l.start(); });
    }
    // .sx-progress reads root.scrollHeight and only listens to scroll/resize.
    if (!quiet) attempt(function () { root.dispatchEvent(new Event('resize')); });
    return currentY();
  }

  /* A restored offset can exceed the document height while late images and
     fonts are still sizing, so retry — bounded, and abandoned the moment a
     newer navigation takes the token, so two restores never fight. */
  function restoreScroll(y, token) {
    if (Math.abs(applyScroll(y) - y) < 2) return;
    var tries = 0;
    (function again() {
      if (token !== seq) return;
      if (++tries > 12) return;           // ~200ms at 60fps
      if (Math.abs(currentY() - y) < 2) return;
      applyScroll(y, true);
      requestAnimationFrame(again);
    }());
  }

  function scrollToHash(hash) {
    var raw;
    try { raw = decodeURIComponent(hash.slice(1)); } catch (e) { raw = hash.slice(1); }
    if (!raw) { applyScroll(0); return null; }
    var el = doc.getElementById(raw);
    if (!el) attempt(function () { el = doc.querySelector('[name="' + (root.CSS && CSS.escape ? CSS.escape(raw) : raw) + '"]'); });
    // No such target. The native behaviour for #does-not-exist is to do
    // NOTHING; v1 scrolled to the top, so a stale in-page anchor threw the
    // user out of their place for no reason. Match the browser.
    if (!el) { log('hash target not found', raw); return null; }
    // scroll-padding-top in the stylesheet only affects scroll-into-view
    // operations. It does nothing for scrollTo(0, y) and nothing for
    // Lenis, so the bar height has to come off explicitly.
    applyScroll(el.getBoundingClientRect().top + currentY() - barOffset());
    return el;
  }

  /* ------------------------------------------------------------------ *
   * 7. OVERLAY TEARDOWN
   *
   * Everything here lives OUTSIDE <main> and therefore survives the swap.
   * That is the problem: a modal left open across a navigation keeps a
   * focus trap pointed at nodes that no longer exist, keeps a click
   * listener on <body> (a permanent GC root, so the whole detached
   * subtree is retained), and — worst — keeps body.overflow-hidden, which
   * makes the incoming page completely unscrollable with nothing on it
   * that will ever remove the class.
   *
   * A stale overlay does not just leak, it misbehaves: a detached
   * DetailsModal's body-click handler will decide a click on the NEW page
   * is "outside itself", call close(), and strip overflow-hidden off a
   * legitimately open cart drawer.
   *
   * Note what is NOT here: nothing reads or writes cart CONTENTS. Closing
   * a drawer is a UI operation on an element that keeps its own state.
   * See §14.
   * ------------------------------------------------------------------ */
  function closeOverlays() {
    // Cart drawer. Dawn's close() calls removeTrapFocus(this.activeElement),
    // and activeElement may be a button in the outgoing <main>. focus() on
    // a detached node is a silent no-op that dumps focus to <body>, so null
    // it and let the router own focus placement.
    each(doc.querySelectorAll('cart-drawer'), function (d) {
      attempt(function () {
        d.activeElement = null;
        if (typeof d.close === 'function') d.close();
      });
    });

    each(doc.querySelectorAll('cart-notification'), function (n) {
      attempt(function () { if (typeof n.close === 'function') n.close(); });
    });

    // Header search. close(false) = do not restore focus to the summary.
    each(doc.querySelectorAll('details-modal'), function (m) {
      attempt(function () { if (typeof m.isOpen === 'function' && m.isOpen()) m.close(false); });
    });

    // Mobile menu. This MUST be explicit: in this Dawn build
    // MenuDrawer.onFocusOut() calls closeMenuDrawer() with no argument and
    // the method body is gated on `event !== undefined`, so focusout never
    // actually closes it. Passing an event closes it, clears
    // overflow-hidden-<breakpoint>, and moves no focus.
    each(doc.querySelectorAll('menu-drawer, header-drawer'), function (d) {
      attempt(function () {
        if (typeof d.closeMenuDrawer === 'function') d.closeMenuDrawer(new Event('sx:router'), false);
      });
    });

    // Header dropdowns. DetailsDisclosure only auto-closes on focusout if
    // the summary held focus, so mouse-opened dropdowns stay open forever.
    each(doc.querySelectorAll('details-disclosure, header-menu'), function (d) {
      attempt(function () { if (typeof d.close === 'function') d.close(); });
    });

    // Anything left open outside <main> that no custom element owns.
    each(doc.querySelectorAll('body > details[open], .shopify-section-group-header-group details[open]'), function (d) {
      d.removeAttribute('open');
      var s = d.querySelector('summary');
      if (s) s.setAttribute('aria-expanded', 'false');
    });

    // Release any surviving trap WITHOUT moving focus. Dawn's
    // removeTrapFocus() with no argument removes the focusin/focusout/
    // keydown document listeners and focuses nothing — exactly the
    // primitive we want, since focus placement is ours.
    attempt(function () { if (typeof root.removeTrapFocus === 'function') root.removeTrapFocus(); });

    // Two distinct scroll-lock class families. Leaving either one on is a
    // hard operability failure: the user cannot reach anything below the
    // fold and has no way to recover.
    doc.body.classList.remove('overflow-hidden');
    doc.body.className = doc.body.className.replace(/\boverflow-hidden-\S+/g, '').replace(/\s{2,}/g, ' ').trim();

    // Dawn's focus-visible polyfill (legacy Safari) parks .focused on the
    // outgoing node.
    var f = doc.querySelector('.focused');
    if (f) f.classList.remove('focused');
  }

  /* ------------------------------------------------------------------ *
   * 8. SECTION TEARDOWN
   * ------------------------------------------------------------------ */
  function sectionsIn(scope) {
    return slice(scope.querySelectorAll('[id^="shopify-section-"]'));
  }

  function sectionId(el) {
    return el.id.replace(/^shopify-section-/, '');
  }

  /* DO NOT "SIMPLIFY" THIS FUNCTION AWAY. It is the single line that
     separates a working router from one that silences the radio on every
     click. spatialx-radio.js PAUSES THE AUDIO on a shopify:section:unload
     whose target contains #sx-radio. Those nodes live in header-group and
     therefore can never be inside <main> today — but the whole point of
     this file is that the audio survives, and a section-group reshuffle in
     the customizer must not be able to defeat it silently. Refuse to fire
     lifecycle events on those subtrees, always, belt and braces. */
  function isPersistent(el) {
    return !!(el.querySelector && (el.querySelector('#sx-radio') || el.querySelector('#sx-now-playing')));
  }

  function fireSection(el, name) {
    if (isPersistent(el)) { log('refused to fire ' + name + ' on a persistent section', el.id); return; }
    attempt(function () {
      el.dispatchEvent(new CustomEvent('shopify:section:' + name, {
        bubbles: true,
        detail: { sectionId: sectionId(el), load: name === 'load' }
      }));
    });
  }

  /* A <video> removed from the document keeps downloading. Measured on
     this theme: an element pulled mid-fetch carried on to its buffer
     target and parked 12 seconds of 720p that nobody will ever see. The
     homepage can have up to fifteen of them.
     removeAttribute('src') alone is NOT enough — load() is what runs the
     media element load algorithm and aborts the in-flight fetch, and any
     <source> children must go first or load() just re-selects one. */
  function teardownMedia(scope) {
    each(scope.querySelectorAll('video, audio'), function (v) {
      attempt(function () {
        v.pause();
        v.removeAttribute('src');
        each(v.querySelectorAll('source'), function (s) { s.remove(); });
        v.load();
      });
    });
    each(scope.querySelectorAll('iframe'), function (f) {
      attempt(function () { f.src = 'about:blank'; });
    });
  }

  function teardownMain() {
    var scope = main;

    // 1. The theme's own hooks, per section. Five separate pieces of this
    //    codebase already implement teardown against exactly this event.
    each(sectionsIn(scope), function (s) { fireSection(s, 'unload'); });

    // 2. Dawn holders that have no disconnectedCallback but do expose a
    //    handle. SlideshowComponent's setInterval is the one genuinely
    //    engine-independent JS-heap leak in Dawn; pause() clears it.
    each(scope.querySelectorAll('slideshow-component'), function (el) {
      attempt(function () { el.pause(); });
      attempt(function () { clearTimeout(el.setPositionTimeout); });
    });
    each(scope.querySelectorAll('product-recommendations'), function (el) {
      attempt(function () { if (el.observer) el.observer.disconnect(); });
    });

    // 2b. Instances that own an OFF-DOM `new Audio()`. teardownMedia below
    //     cannot see these: querySelectorAll('audio') only matches elements
    //     that were inserted into the document, and downconverter.js keeps
    //     its player in `this.audio` on the instance, never in the DOM.
    //     That asset registers no shopify:section:unload handler and its
    //     destroy() is reachable only from init(), so without this the PDP
    //     demo keeps playing — audibly, walking the whole tracklist via its
    //     `ended` handler — on every page the user visits afterwards, with
    //     no control anywhere in the DOM to stop it, and its AudioContext
    //     is never closed.
    //
    //     RULE, not a special case: every entry in REBOOT (§10) must have a
    //     matching teardown here. A reboot hook without one is by
    //     definition "construct instance N+1 while instance N is still
    //     alive", and for an audio module that means two players.
    each(scope.querySelectorAll('.sx-downconverter'), function (el) {
      attempt(function () {
        if (el.__sxdc && typeof el.__sxdc.destroy === 'function') el.__sxdc.destroy();
      });
    });

    if (root.SpatialxDjDeck && root.SpatialxDjDeck.destroy) {
      attempt(function () { root.SpatialxDjDeck.destroy(scope); });
    }

    // 3. Media.
    teardownMedia(scope);

    // 4. Anything a section portalled out to <body> — the artists index
    //    appends its related-releases panel there, and a second visit
    //    would append a second node with the same id. Its own teardown
    //    handles it via the unload above; this is the safety net, and the
    //    convention for anything added later.
    each(doc.querySelectorAll('body > [data-sx-portal]'), function (el) { el.remove(); });

    // 5. Per-section abort scopes handed out by sxRouter.scope().
    each(sectionsIn(scope), function (s) {
      if (s.__sxAbort) attempt(function () { s.__sxAbort.abort(); });
    });

    // 6. Reveal observer.
    unmountMotion(scope);
  }

  /* Offered to section scripts so they can bind document/window listeners
     that actually get cleaned up. The failure pattern this fixes is worth
     naming, because three scripts on this site have it: an idempotency
     guard stored on a DOM node inside <main> cannot protect a listener
     registered on document or window. The node dies with the swap, the
     guard resets, the listener persists — one more copy per visit,
     forever, each pinning a dead subtree.
     Usage, inside a section script:
       var signal = window.sxRouter.scope(root.closest('[id^="shopify-section-"]'));
       document.addEventListener('visibilitychange', fn, { signal: signal });
       signal.addEventListener('abort', function () { mo.disconnect(); });
     Until the three leaky sections adopt it, MAX_SWAPS (§18) is the
     backstop: one real page load per 60 clicks resets every leaked
     listener and turns unbounded growth into a finite worst case. */
  function scopeFor(sectionEl) {
    if (!sectionEl) return null;
    if (sectionEl.__sxAbort) attempt(function () { sectionEl.__sxAbort.abort(); });
    var ac = new AbortController();
    sectionEl.__sxAbort = ac;
    return ac.signal;
  }

  /* ------------------------------------------------------------------ *
   * 9. REVEAL ANIMATIONS (window.sxMotion)
   *
   * This is a HARD boot precondition, checked in boot(), not an optional
   * nicety wrapped in attempt(). The reason is specific and it has bitten
   * this theme: the older spatialx-motion.js still on some theme versions
   * exports no mount/unmount, but it DOES inject
   * `body.sx-js .sx-reveal{opacity:0}` globally with a one-shot
   * DOMContentLoaded observer. Arm the router against that asset and every
   * swapped-in section is permanently invisible, with zero errors — a
   * blank page and a clean console. Refusing to arm is the only safe
   * answer.
   *
   * These helpers are the belt to that gate's braces: if sxMotion somehow
   * disappears mid-session, force-reveal rather than leave the page blank.
   * ------------------------------------------------------------------ */
  function motionAPI() {
    var m = root.sxMotion;
    if (m && typeof m === 'object' && typeof m.mount === 'function' && typeof m.unmount === 'function') return m;
    return null;
  }

  function forceReveal(scope) {
    if (!doc.body.classList.contains('sx-js')) return;   // nothing is hidden; nothing to reveal
    log('sxMotion missing mid-session — force-revealing swapped content');
    each(scope.querySelectorAll('.sx-reveal'), function (el) { el.classList.add('sx-in'); });
    if (scope.classList && scope.classList.contains('sx-reveal')) scope.classList.add('sx-in');
  }

  function mountMotion(scope) {
    var m = motionAPI();
    if (!m) { forceReveal(scope); return; }
    attempt(function () { m.mount(scope); });
  }

  function unmountMotion(scope) {
    var m = motionAPI();
    if (m) attempt(function () { m.unmount(scope); });
  }

  /* ------------------------------------------------------------------ *
   * 9b. AUDIO EXCLUSIVITY BUS
   *
   * This site has an exclusivity contract on window: whoever starts
   * playing dispatches sx:audio-play {source, owner} and everybody else
   * pauses. The radio bar and the card previews both implement it. Two
   * surfaces never joined it:
   *
   *   - the PDP's full-stream <audio controls>, a bare native player that
   *     dispatches nothing and listens for nothing. Radio playing + press
   *     play on the stream = both audible. Stream playing + press play on
   *     the radio = both audible.
   *   - anything an in-<main> section adds later with the same shape.
   *
   * That predates the router, but the router makes it the router's
   * problem: after a swap the in-<main> player is the ONLY surface a
   * routed navigation stops, so "the full track stops when I click a link
   * but the 30-second demo does not" is arbitrary from the user's side.
   *
   * The adapter belongs long-term in assets/spatialx-card-actions.js,
   * which already owns the bus and is head-loaded for the document
   * lifetime. It lives here for now because that asset is not part of this
   * change. It is installed only when the router is armed.
   *
   * 'play' does NOT bubble, so the listener must be capture-phase on
   * document. Muted elements opt out — all sixteen cinema films on the
   * homepage are muted and must not join a bus they cannot contend on —
   * and data-sx-offbus is the explicit escape for anything else. No loop
   * risk: pause() dispatches 'pause', never 'play'.
   * ------------------------------------------------------------------ */
  function installMediaBus() {
    doc.addEventListener('play', function (e) {
      var el = e.target;
      if (!el || (el.tagName !== 'AUDIO' && el.tagName !== 'VIDEO')) return;
      if (el.muted || el.hasAttribute('data-sx-offbus')) return;
      attempt(function () {
        root.dispatchEvent(new CustomEvent('sx:audio-play', { detail: { source: 'media', owner: el } }));
      });
    }, true);

    root.addEventListener('sx:audio-play', function (e) {
      var owner = e.detail && e.detail.owner;
      each(doc.querySelectorAll('audio, video'), function (el) {
        if (el === owner || el.muted || el.paused) return;
        attempt(function () { el.pause(); });
      });
    });
  }

  /* CARD PREVIEWS ACROSS A SWAP — a decision, written down so it stops
     looking like an accident. spatialx-card-actions.js pauses its preview
     on `pagehide`, which a routed navigation never fires, so the preview
     now plays on under the next page. We KEEP that behaviour: the preview
     is owned by #sx-now-playing, which lives outside <main>, survives the
     swap and still shows the track and still toggles it. Pausing it here
     would make the router the only thing on the site that stops audio the
     user deliberately started, which is the opposite of this file's
     purpose. The inconsistency with a hard fallback is inherent — that
     path unloads the document — and the radio's resume logic covers it.
     If someone later decides the preview must stop on routed navigation
     too, the hook already exists: card-actions can listen for the
     sx:router:navigate event this file dispatches after every commit. Do
     not "fix" it by reaching into that module's closure from here. */

  /* ------------------------------------------------------------------ *
   * 10. SCRIPT REVIVAL
   *
   * We clone script nodes rather than eval() or new Function(). CSP is not
   * the reason — this storefront ships no script-src — the reasons are:
   *   - new Function cannot express a src= script at all, and two of the
   *     scripts inside <main> are src=;
   *   - top-level var/function inside new Function land in the function's
   *     scope, not on window, so semantics silently diverge from parser
   *     execution the moment a section ships a top-level var;
   *   - cloned nodes keep source maps, real stack frames, document
   *     .currentScript and their DOM position, all for free;
   *   - Shopify can add a storefront script-src at any time and cloning
   *     survives that where eval does not.
   *
   * The mechanism that makes it correct: a freshly created
   * document.createElement('script') has its "already started" flag clear,
   * so insertion always executes. That sidesteps every question about
   * whether an innerHTML-produced node is executable — we never insert
   * those, we build new ones from their attributes and text.
   * ------------------------------------------------------------------ */

  // Whitelist, not blacklist. The two data islands on this site are 90 KB
  // and 172 KB of application/json and must stay inert.
  var RUNNABLE = { '': 1, 'text/javascript': 1, 'application/javascript': 1, 'module': 1 };

  function isRunnable(node) {
    return RUNNABLE[(node.getAttribute('type') || '').trim().toLowerCase()] === 1;
  }

  // Seeded from the document at boot so we never re-run an asset the
  // parser already ran. That is the path that would otherwise re-execute a
  // file containing customElements.define and throw.
  var executedSrc = {};
  function seedSrc() {
    each(doc.querySelectorAll('script[src]'), function (s) {
      attempt(function () { executedSrc[new URL(s.src, root.location.href).href] = true; });
    });
  }

  /* For an asset that is already resident, re-running the file is wrong
     but doing nothing is also wrong — the new markup needs booting. These
     hooks are the sanctioned middle, and both are grounded in exports the
     assets actually ship. Both boots are idempotent, which matters
     because shopify:section:load will reach some of them a second time.
     Every entry here MUST have a matching teardown in teardownMain(). */
  var REBOOT = [
    [/spatialx-dj-deck\.js/, function (scope) {
      if (root.SpatialxDjDeck && root.SpatialxDjDeck.mount) root.SpatialxDjDeck.mount(scope);
    }],
    [/spatialx-home-cinema\.js/, function (scope) {
      if (root.__sxcHomeController && root.__sxcHomeController.boot) root.__sxcHomeController.boot(scope);
    }],
    [/downconverter\.js/, function (scope) {
      if (!root.SXDownconverter || !root.SXDownconverter.init) return;
      each(scope.querySelectorAll('.sx-downconverter'), function (el) {
        if (!el.__sxdc) root.SXDownconverter.init(el);
      });
    }]
  ];

  function reboot(href, scope) {
    for (var i = 0; i < REBOOT.length; i++) {
      if (REBOOT[i][0].test(href)) { attempt(function () { REBOOT[i][1](scope); }); return true; }
    }
    return false;
  }

  /* Two things here are load-bearing and both were wrong in v1.
   *
   * 1. executedSrc[href] is recorded ON LOAD, never before insertion. v1
   *    marked it at the top of the function, so a script node detached by
   *    a newer swap — the user clicked away before a CDN asset landed —
   *    was recorded as executed without ever running. Every later visit
   *    then skipped it as "resident". On /collections that permanently
   *    kills show-more.js: pagination renders as an inert unknown element
   *    for the rest of the session, with no console error. Marking on load
   *    also means a 404 or a reset connection leaves the asset eligible to
   *    be tried again on the next visit, which is the honest state.
   *
   * 2. One exit, guarded by `done`, with a hard cap. A script element
   *    whose fetch stalls (captive portal, CDN black-hole, connection
   *    reset with no RST) fires neither load nor error, and it is NOT
   *    covered by the navigation's AbortController — that signal is only
   *    on the document fetch. Without the cap the promise chain never
   *    settles AND never rejects, so commit's inner .then never runs and
   *    the outer .catch can never fire: swapped, painted, never booted, no
   *    section:load, html.sx-router-swapping never removed, and on
   *    /products/* the inline add-to-cart interceptor never installed — so
   *    the buy form reverts to a native POST, which is a full page load,
   *    which kills the audio. That is the one failure this file exists to
   *    prevent, so it gets a timeout, not a comment.
   */
  function runOne(node, scope) {
    return new Promise(function (resolve) {
      if (!isRunnable(node)) { resolve(); return; }

      var src = node.getAttribute('src');
      var href = null;
      if (src) {
        try { href = new URL(src, root.location.href).href; } catch (e) { resolve(); return; }

        if (executedSrc[href]) {
          // Already resident. Re-running it could redefine a custom
          // element, restart a singleton, or double-bind. Boot the new
          // markup instead, and if we have no hook for this file, leave it
          // alone and say so — a silent skip is better than a throw that
          // aborts the rest of the navigation.
          if (!reboot(href, scope)) log('resident asset skipped, no reboot hook', href);
          node.remove();
          resolve();
          return;
        }
      }

      var s = doc.createElement('script');
      for (var i = 0; i < node.attributes.length; i++) {
        s.setAttribute(node.attributes[i].name, node.attributes[i].value);
      }
      s.textContent = node.textContent;
      s.setAttribute('data-sx-revived', '1');

      // MANDATORY. A dynamically inserted script defaults to async and
      // silently ignores defer, so without this spatialx-home-cinema.js
      // could run before or after the inline scripts nondeterministically.
      // async=false restores document order.
      s.async = false;

      if (href) {
        var done = false;
        var cap = null;
        var settle = function () {
          if (done) return;
          done = true;
          clearTimeout(cap);
          resolve();
        };

        // Marked here and nowhere else: this fires only if the file
        // actually loaded and executed. Kept outside settle() on purpose,
        // so a script that lands AFTER the timeout is still recorded as
        // resident rather than being run twice on the next visit.
        s.addEventListener('load', function () {
          executedSrc[href] = true;
          settle();
        }, { once: true });
        s.addEventListener('error', function () { log('asset failed', src); settle(); }, { once: true });

        // Detached by a newer swap. Do NOT insert and do NOT mark.
        if (!node.parentNode) { settle(); return; }

        cap = setTimeout(function () { log('script timed out, moving on', src); settle(); }, SCRIPT_MS);
        node.parentNode.replaceChild(s, node);
      } else {
        if (node.parentNode) node.parentNode.replaceChild(s, node);
        resolve();   // inline scripts execute synchronously on insertion
      }
    });
  }

  /* The token check is not decoration. Without it a superseded page's
     inline scripts keep executing against the NEW page's DOM, binding
     stray document/window listeners against markup they were never
     written for. */
  function runScripts(scope, token) {
    var nodes = slice(scope.querySelectorAll('script'));
    var chain = Promise.resolve();
    each(nodes, function (node) {
      chain = chain.then(function () {
        if (token !== seq) return undefined;   // a newer navigation owns the DOM now
        return runOne(node, scope);
      });
    });
    return chain;
  }

  /* Two ways a duplicate define can happen: a future section inline-defines
     an element (the second visit throws, and because the throw is uncaught
     it aborts the rest of that script, silently killing everything below
     it), or we re-run a resident asset that defines one (prevented by
     executedSrc above, but defence in depth is cheap here).
     TRADE-OFF, stated plainly: the second class body is discarded, so new
     markup upgrades against the first definition seen this document
     lifetime. A component changed by a deploy will not take effect until a
     hard reload. That is the correct behaviour for a router — the registry
     is document-scoped, only the markup changes — and it is strictly
     better than an uncaught throw. */
  function guardDefine() {
    if (root.__sxDefineGuard || !root.customElements) return;
    root.__sxDefineGuard = true;
    var native = customElements.define.bind(customElements);
    customElements.define = function (name, ctor, options) {
      if (customElements.get(name)) { log('duplicate define skipped', name); return; }
      return native(name, ctor, options);
    };
  }

  /* ------------------------------------------------------------------ *
   * 11. ASSETS INSIDE <main>
   *
   * The collection, cart and search templates carry 7–11 <link
   * rel="stylesheet"> tags inside <main>. Unlike <script>, these DO load
   * when inserted via innerHTML — but asynchronously, so the swap paints
   * unstyled first. Hoisting them into <head> and waiting (briefly)
   * before the swap removes the flash entirely. Dawn's section CSS is
   * class-scoped, so a stylesheet that outlives its section is inert.
   *
   * This pass ALSO emits rel=preload as=script for the src= scripts we are
   * about to revive. Read that carefully, because it is the one thing in
   * this file that rhymes with the prefetch that was deleted, and it is
   * not the same thing: these are assets of a document we have ALREADY
   * fetched and are ALREADY committing, requested at commit time, never
   * on hover, never speculatively, capped, and for a page the user has
   * definitely asked for. Revival is a serial chain (document order is
   * mandatory), so opening the connections in parallel is the only way to
   * stop the last script in the chain waiting on the first one's round trip.
   *
   * No crossOrigin on the preload link: these are same-origin, and setting
   * it would mismatch the subsequent non-CORS script fetch and download
   * every file twice.
   * ------------------------------------------------------------------ */
  var loadedStyles = {};

  /* Seed ONLY from <head>. This distinction is the whole ballgame.
     A <link rel=stylesheet> that lives inside <main> is NOT durable — it is
     destroyed with the outgoing markup on the very first swap. Seeding it as
     "loaded" tells preloadAssets the sheet is safely resident when it is
     about to be deleted, and on the return visit preloadAssets then skips
     hoisting it AND strips the incoming copy, leaving the sheet in neither
     place. The homepage carries spatialx-home-cinema.css (23 KB, and the
     source of essentially all of its layout) inside <main>, so going
     home -> anywhere -> home rendered the homepage completely unstyled. */
  function seedStyles() {
    each(doc.head.querySelectorAll('link[rel~="stylesheet"][href]'), function (l) {
      attempt(function () { loadedStyles[new URL(l.href, root.location.href).href] = true; });
    });
  }

  /* Is this sheet genuinely resident in <head> right now? Never trust the
     bookkeeping alone — another script, or a previous teardown, may have
     removed it. */
  function headHasStyle(href) {
    var found = false;
    each(doc.head.querySelectorAll('link[rel~="stylesheet"][href]'), function (l) {
      attempt(function () {
        if (new URL(l.href, root.location.href).href === href) found = true;
      });
    });
    return found;
  }

  function preloadAssets(nextMain) {
    // Previous preload hints are spent the moment their navigation
    // committed; dropping them keeps <head> from growing across a session.
    each(doc.head.querySelectorAll('link[rel="preload"][data-sx-hoisted]'), function (l) { l.remove(); });

    var wanted = [];
    each(nextMain.querySelectorAll('link[rel~="stylesheet"][href]'), function (l) {
      var href;
      try { href = new URL(l.getAttribute('href'), root.location.href).href; } catch (e) { return; }
      if (!loadedStyles[href]) {
        loadedStyles[href] = true;
        wanted.push(href);
      }
      /* Strip the incoming copy ONLY when the sheet is verifiably resident
         in <head>, where it will survive the swap. Otherwise this removal
         is not deduplication — it is deletion, and the page renders without
         its stylesheet. Verified against the live DOM rather than against
         loadedStyles, so stale bookkeeping can never cost us a stylesheet;
         the worst case here is one duplicate download, which is enormously
         cheaper than an unstyled page. */
      if (headHasStyle(href) && l.parentNode) l.parentNode.removeChild(l);
    });

    var budget = PRELOAD_MAX - wanted.length;
    if (budget > 0) {
      each(nextMain.querySelectorAll('script[src]'), function (n) {
        if (budget <= 0) return;
        var href;
        try { href = new URL(n.getAttribute('src'), root.location.href).href; } catch (e) { return; }
        if (executedSrc[href]) return;               // resident: runOne will skip it anyway
        if (href.indexOf(root.location.origin) !== 0) return;
        // as="script" is wrong for a module and would fetch it twice; there
        // are none inside <main> today, and if one appears it simply misses
        // the hint rather than paying for it.
        if ((n.getAttribute('type') || '').toLowerCase() === 'module') return;
        budget -= 1;
        var hint = doc.createElement('link');
        hint.rel = 'preload';
        hint.as = 'script';
        hint.href = href;
        hint.setAttribute('data-sx-hoisted', '1');
        doc.head.appendChild(hint);
      });
    }

    if (!wanted.length) return Promise.resolve();

    return new Promise(function (resolve) {
      var pending = wanted.length;
      var settled = false;

      /* One exit, and the deadline is cleared ONLY here. Clearing it from
         a per-link load handler is a trap worth spelling out: the first
         sheet to arrive would disarm the timeout protecting the last one,
         and a single stylesheet that never fires load or error would then
         hang the navigation forever with no error and no fallback. That
         bug was live in this file until it was caught by actually driving
         a swap in a browser. */
      function finish() {
        if (settled) return;
        settled = true;
        clearTimeout(cap);
        resolve();
      }
      function done() { if (--pending <= 0) finish(); }

      // Capped: a slow CDN delays the swap by at most this much, then we
      // paint anyway and accept a brief flash. A navigation must never be
      // blocked on a stylesheet.
      var cap = setTimeout(finish, 600);

      each(wanted, function (href) {
        var link = doc.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-sx-hoisted', '1');
        link.addEventListener('load', done, { once: true });
        link.addEventListener('error', done, { once: true });
        doc.head.appendChild(link);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 12. <head> SYNC
   *
   * document.title is not polish: without it the router fails SC 2.4.2
   * Page Titled outright, and browser history and tab labels go wrong.
   * The canonical link is not polish either — trekkie reads it live at
   * emit time for its `url` field, so a stale canonical means every
   * virtual pageview is attributed to the previous page.
   *
   * pushState and this function must BOTH complete before any analytics
   * call. Get that order wrong and every event reports the old URL.
   *
   * Route metadata is synchronized as one set: description, robots,
   * Open Graph, Twitter, alternates, and the single route JSON-LD graph.
   * Not synced, deliberately: checkout API, digital-wallet and perf-kit
   * tags (constant across templates), and document.referrer (fixed for the
   * document's lifetime — do NOT monkey-patch it with defineProperty,
   * which is globally visible and would surprise every other script).
   * ------------------------------------------------------------------ */
  var HEAD_META = [
    'meta[name="description"]',
    'meta[name="robots"]',
    'meta[property^="og:"]',
    'meta[name^="twitter:"]',
    'link[rel="alternate"]'
  ].join(',');
  var HEAD_ROUTE_SCHEMA = 'script[type="application/ld+json"][data-sx-route-schema]';

  function syncHead(nextDoc) {
    if (nextDoc.title && nextDoc.title !== doc.title) doc.title = nextDoc.title;

    // SC 3.1.1 Language of Page. Inert today (single locale) and one line.
    var lang = nextDoc.documentElement.getAttribute('lang');
    if (lang && lang !== doc.documentElement.getAttribute('lang')) {
      doc.documentElement.setAttribute('lang', lang);
    }

    var nextCanon = nextDoc.querySelector('link[rel="canonical"]');
    var liveCanon = doc.querySelector('link[rel="canonical"]');
    if (nextCanon) {
      if (!liveCanon) {
        liveCanon = doc.createElement('link');
        liveCanon.rel = 'canonical';
        doc.head.appendChild(liveCanon);
      }
      liveCanon.setAttribute('href', nextCanon.getAttribute('href'));
    }

    // Replace the social/description block as a set. These are all
    // theme-emitted before content_for_header, so they are clean to swap
    // wholesale rather than key-by-key.
    each(doc.head.querySelectorAll(HEAD_META), function (el) { el.parentNode.removeChild(el); });
    each(nextDoc.head ? nextDoc.head.querySelectorAll(HEAD_META) : [], function (el) {
      doc.head.appendChild(doc.importNode(el, true));
    });

    // The audited theme emits one route graph in <head>. Replace it on every
    // virtual navigation so product/article/profile entities never describe
    // the previous URL in the live document.
    each(doc.head.querySelectorAll(HEAD_ROUTE_SCHEMA), function (el) { el.parentNode.removeChild(el); });
    each(nextDoc.head ? nextDoc.head.querySelectorAll(HEAD_ROUTE_SCHEMA) : [], function (el) {
      doc.head.appendChild(doc.importNode(el, true));
    });
  }

  /* ------------------------------------------------------------------ *
   * 13. ANALYTICS
   *
   * Read the ground truth before changing anything here, because most of
   * the obvious approaches do not work on this shop:
   *
   *   - webPixelsManager.init() cannot be called twice; the second call is
   *     a logged no-op, and it reads its own dataset config anyway so you
   *     cannot inject events through it.
   *   - Shopify.analytics.publish('page_viewed') returns FALSE here. The
   *     strict branch rejects standard event names unless beta flag
   *     6b3fd603 is enabled, and this shop ships d5bdd5d0 and 656605ce.
   *     No throw, no warning — it just silently does nothing.
   *   - The trekkie shim reaches Shopify's monorail pipeline but never
   *     reaches app or custom pixels.
   *
   * So: Shopify's own admin analytics (Reports, Live View, sessions) stays
   * correct, because we replay verbatim the trekkie calls the server told
   * the browser to emit for the destination page. App pixels see one
   * page_viewed per document and nothing more; that gap is not fixable
   * from theme code and is called out in the handover notes.
   *
   * The payloads are extracted from the fetched HTML with a balanced-brace
   * scanner. A lazy /\{.*?\}/ breaks the moment a payload nests, and the
   * product payload does. Order is fixed by trekkie's own replay queue:
   * track() before page().
   * ------------------------------------------------------------------ */

  // Read a balanced (...) argument list starting at the char after '('.
  // String-aware so a ')' inside a title cannot end it early.
  function readArgs(s, i) {
    var depth = 1, out = '', q = null, esc = false;
    while (i < s.length) {
      var c = s.charAt(i);
      if (q) {
        if (esc) esc = false;
        else if (c === '\\') esc = true;
        else if (c === q) q = null;
      } else if (c === '"' || c === "'") {
        q = c;
      } else if (c === '(' || c === '{' || c === '[') {
        depth++;
      } else if (c === ')' || c === '}' || c === ']') {
        depth--;
        if (depth === 0 && c === ')') return out;
      }
      out += c;
      i++;
    }
    return null;
  }

  function splitTop(args) {
    var parts = [], depth = 0, cur = '', q = null, esc = false;
    for (var i = 0; i < args.length; i++) {
      var c = args.charAt(i);
      if (q) {
        if (esc) esc = false;
        else if (c === '\\') esc = true;
        else if (c === q) q = null;
      } else if (c === '"' || c === "'") { q = c; }
      else if (c === '(' || c === '{' || c === '[') depth++;
      else if (c === ')' || c === '}' || c === ']') depth--;
      else if (c === ',' && depth === 0) { parts.push(cur.trim()); cur = ''; continue; }
      cur += c;
    }
    if (cur.trim()) parts.push(cur.trim());
    return parts;
  }

  function jsonOrNull(s) {
    if (!s || s === 'undefined' || s === 'null') return null;
    try { return JSON.parse(s); } catch (e) { return null; }
  }

  // Balanced-brace read of the object literal starting at html[open] === '{'.
  // Separate from readArgs because that one terminates on ')', not '}'.
  function readObject(html, open) {
    var depth = 0, buf = '', q = null, esc = false;
    for (var i = open; i < html.length; i++) {
      var c = html.charAt(i);
      buf += c;
      if (q) {
        if (esc) esc = false;
        else if (c === '\\') esc = true;
        else if (c === q) q = null;
        continue;
      }
      if (c === '"' || c === "'") q = c;
      else if (c === '{') depth++;
      else if (c === '}') { depth--; if (!depth) return buf; }
    }
    return null;
  }

  function extractAnalytics(html) {
    var out = { page: null, tracks: [], meta: null, currency: null };

    var needle = 'ShopifyAnalytics.lib.';
    var at = 0;
    while ((at = html.indexOf(needle, at)) !== -1) {
      var head = html.slice(at + needle.length, at + needle.length + 8);
      var isPage = head.indexOf('page(') === 0;
      var isTrack = head.indexOf('track(') === 0;
      if (!isPage && !isTrack) { at += needle.length; continue; }
      var open = html.indexOf('(', at + needle.length);
      var args = readArgs(html, open + 1);
      at = open + 1;
      if (!args) continue;
      var parts = splitTop(args);
      if (isPage) {
        if (!out.page) out.page = jsonOrNull(parts[1]) || {};
      } else {
        var name = jsonOrNull(parts[0]);
        if (typeof name !== 'string') continue;
        out.tracks.push({ name: name, props: jsonOrNull(parts[1]) || {}, opts: jsonOrNull(parts[4]) });
      }
    }

    // ShopifyAnalytics.meta is merged additively by the server block
    // (`for (var attr in meta) ...`), which means it never deletes — a
    // product page followed by a home page leaves a stale `.product`.
    var mAt = html.indexOf('var meta = {');
    if (mAt !== -1) out.meta = jsonOrNull(readObject(html, html.indexOf('{', mAt)));

    var cur = html.match(/ShopifyAnalytics\.meta\.currency\s*=\s*['"]([A-Z]{3})['"]/);
    if (cur) out.currency = cur[1];

    return out;
  }

  var META_KEYS = ['product', 'products', 'page', 'collection', 'variant', 'selectedVariantId'];

  function reportPageview(html, prevUrl) {
    // A separately-shipped module wins if it is present — this built-in is
    // the fallback so the router is genuinely self-contained.
    if (root.sxRouterAnalytics && typeof root.sxRouterAnalytics.commit === 'function') {
      attempt(function () { root.sxRouterAnalytics.commit(html, null, prevUrl); });
      return;
    }

    var x = attempt(function () { return extractAnalytics(html); });
    if (!x) return;

    attempt(function () {
      var SA = root.ShopifyAnalytics;
      if (!SA) return;
      if (SA.meta) {
        for (var i = 0; i < META_KEYS.length; i++) delete SA.meta[META_KEYS[i]];
        if (x.currency) SA.meta.currency = x.currency;
        if (x.meta) for (var k in x.meta) if (Object.prototype.hasOwnProperty.call(x.meta, k)) SA.meta[k] = x.meta[k];
      }
      var lib = SA.lib;
      if (!lib || typeof lib.page !== 'function') return;
      // track() before page(), matching the order trekkie's own
      // replayAnalyticsQueue produces on a hard load.
      for (var t = 0; t < x.tracks.length; t++) {
        var tr = x.tracks[t];
        attempt(function () { lib.track(tr.name, tr.props, undefined, undefined, tr.opts || undefined); });
      }
      lib.page(null, x.page || {});
    });

    // The only channel that reaches app pixels and merchant custom pixels
    // at all. A standard event name would be rejected; a custom one is
    // delivered to all_events / all_custom_events subscribers.
    attempt(function () {
      if (!root.Shopify || !root.Shopify.analytics || !root.Shopify.analytics.publish) return;
      var p = (x.page || {});
      root.Shopify.analytics.publish('sx_virtual_page_viewed', {
        pageType: p.pageType || '',
        resourceType: p.resourceType || '',
        resourceId: p.resourceId != null ? String(p.resourceId) : '',
        url: root.location.href,
        path: root.location.pathname,
        title: doc.title,
        internalReferrer: prevUrl || ''
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 14. THERE IS NO CART SYNC HERE. THAT IS THE FEATURE.
   *
   * v1 had a syncCart() that copied #cart-icon-bubble and #CartDrawer
   * markup out of the fetched document into the live page, on the theory
   * that a full page load used to re-render them for free and the router
   * had deleted that free repair.
   *
   * The theory is wrong, and the code could only ever be wrong with it.
   * The fetched document is a server render captured at an arbitrary
   * earlier moment. It has no relationship to the state of the cart at
   * commit time. So the function's whole job was to overwrite live,
   * correct markup with a snapshot that might be older than the user's
   * last add-to-cart. Verified failure, on the shipped CSS:
   *
   *     cart-drawer-items.is-empty + .drawer__footer { display: none }
   *
   * .drawer__footer is the element containing
   * <button name="checkout" form="CartDrawer-Form">. Write a pre-add
   * snapshot back into #CartDrawer and the drawer reverts to "your
   * acquisition list is empty" and THE CHECKOUT BUTTON LEAVES THE DOM,
   * while the item is still sitting in the server cart. The user's next
   * action is to give up or to add the item a second time.
   *
   * The correct answer is not a freshness epoch bolted onto the copy. It
   * is that the cart drawer and the cart bubble live OUTSIDE <main>, are
   * never touched by the swap, and simply keep their real state. Dawn's
   * own add-to-cart flow re-renders both through the cart API
   * (renderContents() → /?sections=cart-drawer,cart-icon-bubble) on every
   * mutation, and that path is completely unaffected by this router:
   * runAdd/addToBag close over the drawer element, which survives, so a
   * POST that lands mid-swap still completes and still re-renders.
   *
   * So: the router does not read, write, or think about cart contents.
   * closeOverlays() closes the drawer as a UI element and touches nothing
   * inside it. If you are reading this because the header count looks
   * stale, the bug is in whatever mutated the cart without calling the
   * theme's own refresh — fix it there. A router that copies cart HTML out
   * of a page fetched at an arbitrary earlier moment can only ever be
   * wrong, and it is wrong in the one place on this site where being wrong
   * costs money.
   * ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ *
   * 15. DAWN'S FACETS
   *
   * Dawn's facets module registers a permanent window popstate handler at
   * module load, and its handler reads event.state.searchParams then calls
   * renderPage(), which opens with an unguarded
   * getElementById('ProductGridContainer'). Once a visitor has loaded a
   * collection or search page, that listener is resident for the whole
   * session — so a Back press on the homepage would throw a TypeError
   * inside a popstate handler.
   * Three stacked defences: our pushState always carries searchParams (see
   * stampState), our popstate listener declines facet-owned entries, and
   * this resyncs the statics after every commit so the !== guard
   * short-circuits. Class declarations are lexical and not on window, so
   * customElements.get is the reliable accessor. */
  function syncFacets(url) {
    attempt(function () {
      var FF = root.FacetFiltersForm || (root.customElements && customElements.get('facet-filters-form'));
      if (!FF) return;
      FF.searchParamsInitial = FF.searchParamsPrev = url.search.slice(1);
    });
  }

  /* ------------------------------------------------------------------ *
   * 16. FOCUS
   *
   * #MainContent already has tabindex="-1", so it is programmatically
   * focusable and stays out of the tab sequence (Dawn's
   * getFocusableElements selector excludes negative tabindex). No new
   * markup needed, and no visible focus ring is required: .focus-none
   * suppresses it, and SC 2.4.7 applies to UI components in the tab order,
   * which a tabindex="-1" container is not. The requirement that DOES bind
   * is SC 2.4.3 Focus Order — focus must not be left on a detached node or
   * dumped to <body>, which is precisely what an unhandled swap does.
   *
   * preventScroll is mandatory. focus() scrolls its target into view by
   * default, which would silently undo the offset we just restored.
   * ------------------------------------------------------------------ */
  function placeFocus(token, hashEl) {
    var target = hashEl || main;
    if (hashEl && !hashEl.hasAttribute('tabindex')) hashEl.setAttribute('tabindex', '-1');
    attempt(function () { target.focus({ preventScroll: true }); });

    // Re-assert once. CartDrawer.open() registers a one-shot transitionend
    // that calls trapFocus, so a navigation started mid-transition can have
    // focus stolen a frame after the swap. Narrow condition on purpose: we
    // only reclaim from body/null/detached, never merely because focus
    // moved outside main — the user may legitimately have tabbed to the
    // header.
    requestAnimationFrame(function () {
      if (token !== seq) return;
      var a = doc.activeElement;
      if (!a || a === doc.body || a === doc.documentElement || !a.isConnected) {
        attempt(function () { if (typeof root.removeTrapFocus === 'function') root.removeTrapFocus(); });
        attempt(function () { target.focus({ preventScroll: true }); });
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * 17. THE COMMIT
   *
   * Order here is load-bearing and every step has a reason:
   *   a. record the outgoing scroll offset, before the history entry moves
   *   b. freeze Lenis, then tear down the outgoing <main>
   *   c. pushState — BEFORE the swap, because CartItems.updateQuantity
   *      builds sections_url from window.location.pathname, and before
   *      analytics because trekkie reads location at emit time
   *   d. swap innerHTML (custom elements upgrade here)
   *   e. sync <head> — title before announce, canonical before analytics
   *   f. scroll, focus, announce, analytics: the user-visible half, done
   *      synchronously so nothing waits on a slow section asset
   *   g. revive scripts, then fire section:load, then re-settle scroll
   * ------------------------------------------------------------------ */
  function commit(payload, ctx) {
    var token = ctx.token;
    var nextDoc = new DOMParser().parseFromString(payload.html, 'text/html');
    var nextMain = nextDoc.querySelector('main#' + MAIN_ID);
    if (!nextMain) throw new Error('response has no main#' + MAIN_ID);

    var prevUrl = root.location.href;
    var url = payload.url;
    var template = nextMain.getAttribute('data-template') || '';

    /* The escape hatch (§4b), read off the response rather than guessed
       from the URL, and applied while the live DOM is still completely
       untouched. Non-counting: an opted-out template is a decision, not a
       failure, and must not feed the circuit breaker. */
    if (templateOptedOut(template)) {
      hardNavigate(url, 'template opted out: ' + template, false, ctx.popped);
      return Promise.resolve();
    }

    return preloadAssets(nextMain).then(function () {
      if (token !== seq) return undefined;

      /* (a) On a popstate this was already captured in onPopState, BEFORE
             currentEntryId moved to the destination entry. Doing it again
             here would write the outgoing page's scrollY into the
             destination's slot — the very slot we are about to restore
             from — and the second Back to that entry would land wrong. */
      if (!ctx.popped) recordScroll();
      persistOffsets();

      /* (b) Freeze the smooth-scroll engine HERE, not at the top of
             navigate(). Lenis binds wheel and touchstart/touchmove
             non-passive and calls preventDefault() unconditionally while
             stopped, so stop() does not merely un-smooth scrolling, it
             kills it — on mobile that is a total freeze. v1 stopped it
             before the fetch, so a slow network froze the page for the
             whole request with no feedback at all. The swap is imminent
             now; a freeze measured in one frame is invisible. */
      attempt(function () { if (root.sxLenis) root.sxLenis.stop(); });
      teardownMain();

      // (c)
      if (!ctx.popped) stampState(false, url);
      else syncFacets(url);      // popstate: the browser already moved us
      rendered = url;

      // (d) innerHTML, never replaceWith: keeping the element preserves
      //     id/tabindex/role, which is what keeps the skip link — SC 2.4.1
      //     Bypass Blocks — pointing at something that exists.
      main.innerHTML = nextMain.innerHTML;
      main.setAttribute('data-template', template);

      // (e) Note what is NOT called here: there is no cart sync. See §14.
      syncHead(nextDoc);
      syncFacets(url);

      // (f)
      var hashEl = null;
      if (typeof ctx.restoreY === 'number') restoreScroll(ctx.restoreY, token);
      else if (url.hash) hashEl = scrollToHash(url.hash);
      else applyScroll(0);

      mountMotion(main);

      main.removeAttribute('aria-busy');
      clearTimeout(loadingTimer);
      placeFocus(token, hashEl);
      announce(T_NAV.replace('[title]', doc.title || ''));
      reportPageview(payload.html, prevUrl);

      // (g) Scripts LAST, and section:load only after they resolve — the
      //     handler a section listens with is registered by the very script
      //     being revived, so firing first lands on nobody.
      return runScripts(main, token).then(function () {
        if (token !== seq) return;
        each(sectionsIn(main), function (s) { fireSection(s, 'load'); });
        mountMotion(main);

        // Sections that just rendered content have changed the document
        // height, so settle the offset once more against the real layout.
        if (typeof ctx.restoreY === 'number') restoreScroll(ctx.restoreY, token);
        else if (!url.hash) applyScroll(0);

        doc.documentElement.classList.remove('sx-nav-pending');
        setTimeout(function () {
          if (token === seq) doc.documentElement.classList.remove('sx-router-swapping');
        }, 300);

        /* A navigation that got all the way here is evidence the router
           works, so it clears the breaker's counter. BREAK_AT therefore
           means two CONSECUTIVE failures, not two failures in a session:
           a systematically broken route still disables the router on its
           second try, while two unrelated flakes an hour apart do not. */
        swaps += 1;
        STATS.committed += 1;
        clearFailures();

        attempt(function () {
          root.dispatchEvent(new CustomEvent('sx:router:navigate', {
            detail: { url: url.href, from: prevUrl, popped: !!ctx.popped, template: template }
          }));
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * 18. NAVIGATE
   * ------------------------------------------------------------------ */

  /* Undo the pre-navigation state without going anywhere. Every path that
     abandons a navigation has to release the scroll freeze — a page left
     with Lenis stopped is a page that cannot be scrolled by wheel or
     touch, with nothing on it that will ever start it again. */
  function releasePending() {
    clearTimeout(loadingTimer);
    if (main) main.removeAttribute('aria-busy');
    doc.documentElement.classList.remove('sx-nav-pending', 'sx-router-swapping');
    attempt(function () { if (root.sxLenis) root.sxLenis.start(); });
  }

  /* The deliberate non-navigation. Used when the request is already known
     to fail: hard-navigating there would replace a working, audio-playing
     document with the browser's own network-error page, and Back from
     there is another error page because the pushState entries died with
     the document. The router is the one component that can know this
     without having committed to leaving. Both channels get the message —
     the live region for screen readers and the toast for everyone else,
     because "the link did nothing" is the worst possible feedback. */
  function stayPut(message, why) {
    log('stayed put: ' + why);
    STATS.stayedPut += 1;
    releasePending();
    announce(message);
    showToast(message);
  }

  /* count=false for a deliberate hand-back (the flag went off mid-session,
     a denied path, an opted-out template, the swap budget), which is not
     evidence that routing is broken and must not feed the breaker. Only
     genuine failures — network, timeout, bad response, commit throw —
     count against it.

     popped=true means the browser has already moved the address bar to
     this URL, so a reload is the honest way to render it; assign() to the
     current URL works too but reload() says what it means. */
  function hardNavigate(url, why, count, popped) {
    log('hard navigation: ' + why, url.href);
    STATS.hardNavigations += 1;
    if (count !== false) {
      STATS.errors += 1;
      var n = bumpFailures();
      if (n >= BREAK_AT) {
        // A systematically failing route must not cost every subsequent
        // click a wasted fetch plus a full load — and every one of those
        // full loads kills the audio and needs a gesture to resume it.
        // Off for the rest of the session; ?sx_router=1 re-arms it.
        sset(K_BREAK, 'off');
        log('circuit breaker tripped after ' + n + ' consecutive failures');
      }
    }
    persistOffsets();
    releasePending();
    if (popped) {
      try { root.location.reload(); return; } catch (e) {}
    }
    // assign, never replace: if a pushState already happened for this
    // click the entry stays in history and Back still works.
    try { root.location.assign(url.href); } catch (e) { root.location.href = url.href; }
  }

  function abortInflight(reason) {
    if (!inflight) return;
    inflight.controller.__sxReason = reason;
    attempt(function () { inflight.controller.abort(); });
    inflight = null;
  }

  function navigate(href, ctx) {
    ctx = ctx || {};
    var url;
    try { url = new URL(href, root.location.href); } catch (e) { return; }

    if (!enabled()) { hardNavigate(url, 'router disabled mid-session', false, ctx.popped); return; }

    /* The public sxRouter.navigate() reaches this function directly, and
       v1 checked the denylist only in the click and popstate handlers. So
       sxRouter.navigate('/cart') fetched /cart, threw on the response
       guard, and burned a real failure — twice and the router was off for
       the session. A denied path is a hand-back, not a fault. */
    if (!isRoutableURL(url)) {
      hardNavigate(url, 'path is browser-owned: ' + url.pathname, false, ctx.popped);
      return;
    }

    /* Bounded residual leak. Three inline sections on this site still bind
       document/window listeners with no teardown (see scopeFor), so a very
       long routed session grows. One real page load per MAX_SWAPS clicks
       resets the document and every leaked listener with it, turning
       unbounded growth into a finite worst case. Non-counting: this is
       housekeeping, not a failure. */
    if (swaps >= MAX_SWAPS) {
      hardNavigate(url, 'swap budget reached (' + swaps + ' swaps)', false, ctx.popped);
      return;
    }

    /* Known-dead request: do not tear down a working page for it. */
    if (root.navigator && root.navigator.onLine === false) {
      /* Back/Forward has ALREADY moved the address bar before we get here.
         Staying put would leave the URL naming a document we never rendered
         — the classic "address bar lies" bug. Hand it to the browser, which
         owns that entry and will show its own offline page for the URL the
         visitor actually asked for. Non-counting: being offline is not the
         router failing. */
      if (ctx.popped) { hardNavigate(url, 'offline during popstate', false, true); return; }
      stayPut(T_OFFLINE, 'navigator.onLine is false');
      return;
    }

    abortInflight('supersede');
    var token = ++seq;
    var controller = new AbortController();
    inflight = { controller: controller, token: token, url: url, popped: !!ctx.popped };

    // The commit half checks this against `seq` at every await boundary to
    // bail out when a newer navigation has superseded it. It has to be put
    // on ctx here — without it every commit compares undefined !== seq and
    // silently abandons itself after the fetch, which looks exactly like a
    // hung network and produces no error at all.
    ctx.token = token;

    // Which half of the navigation a rejection came from. A network-class
    // failure during the FETCH means nothing was touched and we can stay
    // put; the same error class thrown from inside commit means the DOM is
    // already half-moved and the honest recovery is a real load.
    var phase = 'fetch';

    /* The hard ceiling. Nothing else bounds a request that neither
       resolves nor rejects, and without it the page sits in
       sx-nav-pending forever. Tagged 'timeout' so the catch can tell it
       from a supersede: AbortError alone cannot distinguish them on older
       engines. Note the .sx-progress animation is timed to the same 8s, so
       the bar never claims to be finished before we have handed off. */
    var timer = setTimeout(function () {
      if (inflight && inflight.token === token) {
        inflight.controller.__sxReason = 'timeout';
        attempt(function () { inflight.controller.abort(); });
      }
    }, TIMEOUT_MS);

    // Pre-fetch: close every overlay, mark the region busy, start the
    // progress affordance. Deliberately NOT freezing scrolling here —
    // see the note in commit() step (b).
    closeOverlays();
    main.setAttribute('aria-busy', 'true');
    doc.documentElement.classList.add('sx-router-swapping', 'sx-nav-pending');

    clearTimeout(loadingTimer);
    loadingTimer = setTimeout(function () {
      // Past this long with no feedback, a screen-reader user cannot tell
      // whether the click registered at all.
      if (token === seq) announce(T_LOAD);
    }, LOADING_MS);

    fetch(url.href, {
      credentials: 'same-origin',
      redirect: 'follow',
      signal: controller.signal,
      headers: {
        'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        'X-Requested-With': 'sx-router'
      }
    }).then(function (res) {
      if (token !== seq) return null;

      // Validate the FINAL url, not the requested one. This is the only
      // guard that catches a storefront redirect rule pointing a page at
      // /account, /cart or off-origin. Every response goes through here —
      // there is no second, unguarded path into commit any more.
      var final = new URL(res.url || url.href, root.location.href);
      if (final.origin !== root.location.origin) throw new Error('cross-origin redirect -> ' + final.origin);
      if (!isRoutableURL(final)) throw new Error('redirected into a browser-owned path: ' + final.pathname);

      // 404 is explicitly renderable: this theme's 404 ships a real
      // main#MainContent with data-template="404", so routing it gives
      // the user exactly what the browser would have.
      if (!res.ok && res.status !== 404) throw new Error('HTTP ' + res.status);

      var ct = (res.headers.get('content-type') || '').toLowerCase();
      if (ct.indexOf('text/html') === -1) throw new Error('content-type ' + (ct || '(none)'));

      return res.text().then(function (html) {
        return { html: html, url: final, status: res.status };
      });
    }).then(function (payload) {
      if (!payload || token !== seq) return undefined;
      phase = 'commit';
      return commit(payload, ctx);
    })['catch'](function (err) {
      var reason = controller.__sxReason;
      // A supersede is not a failure — the newer navigation owns the
      // outcome and hard-navigating here would cancel it.
      if (err && err.name === 'AbortError' && reason === 'supersede') return;
      if (token !== seq) return;

      /* Offline is the one failure we do not hand to the browser. fetch
         rejects with a TypeError for every network-class fault, and the
         phase check keeps that narrow: a TypeError thrown from inside
         commit means <main> is already half-swapped, and there staying put
         would strand the user on a broken page. */
      var networkish = (root.navigator && root.navigator.onLine === false) ||
        (err && err.name === 'TypeError' && reason !== 'timeout');
      if (phase === 'fetch' && networkish) {
      /* Back/Forward has ALREADY moved the address bar before we get here.
         Staying put would leave the URL naming a document we never rendered
         — the classic "address bar lies" bug. Hand it to the browser, which
         owns that entry and will show its own offline page for the URL the
         visitor actually asked for. Non-counting: being offline is not the
         router failing. */
        if (ctx.popped) { hardNavigate(url, 'offline during popstate', false, true); return; }
        stayPut(T_OFFLINE, (err && err.message) || 'network failure');
        return;
      }

      hardNavigate(url, (err && err.message) || reason || 'fetch failed', true, ctx.popped);
    }).then(function () {
      clearTimeout(timer);
      if (inflight && inflight.token === token) inflight = null;
    }, function () {
      clearTimeout(timer);
      if (inflight && inflight.token === token) inflight = null;
    });
  }

  /* ------------------------------------------------------------------ *
   * 19. CLICK DELEGATION
   *
   * One document-level listener, and it is the ONLY thing on this site
   * that issues a router fetch. Nothing is requested on hover, on touch,
   * on scroll or on a timer. Every branch that returns null hands the
   * click straight back to the browser, unmodified — that is the whole
   * degradation story in one function.
   * ------------------------------------------------------------------ */
  function candidateFor(e) {
    if (e.defaultPrevented) return null;       // somebody already owns this click
    if (e.button !== 0) return null;           // middle/right go native
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;  // new tab / window / download

    var t = e.target;
    if (!t || !t.closest) return null;
    var a = t.closest('a');
    // SVGAElement also matches closest('a') and its .href is an
    // SVGAnimatedString, not a string.
    if (!a || !(a instanceof HTMLAnchorElement)) return null;

    if (a.hasAttribute('download')) return null;
    var tgt = a.getAttribute('target');
    if (tgt && !/^_self$/i.test(tgt)) return null;
    if (/\bexternal\b/i.test(a.getAttribute('rel') || '')) return null;

    // Explicit opt-out, on the link or anything containing it.
    if (a.closest('[data-no-router], [data-sx-router="off"]')) return null;

    // Regions Dawn re-renders itself through the Section Rendering API.
    // Facet links carry the whole filter state in the query string and
    // Dawn already prevents their default.
    if (a.closest('facet-filters-form, .active-facets, .mobile-facets, predictive-search')) return null;

    var url;
    try { url = new URL(a.href, root.location.href); } catch (err) { return null; }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;   // mailto:, tel:, javascript:
    if (url.origin !== root.location.origin) return null;

    // The footer's "Cookie preferences" link. Shopify's consent banner
    // reads this fragment at document load; a pushState fires no
    // hashchange, so routing it would silently break the one control a
    // visitor has over their cookie choice.
    if (url.hash && url.hash.slice(1).toLowerCase() === CONSENT_HASH) return null;

    return url;
  }

  function onClick(e) {
    if (!enabled()) return;
    var url = candidateFor(e);
    if (!url) return;

    var here = new URL(root.location.href);

    // Same document, different fragment. Not a swap — but still ours,
    // because a raw fragment jump is instant and Lenis then lerps away
    // from it, and because a fragment link must move focus, not just
    // scroll (SC 2.4.3).
    if (url.hash && url.pathname === here.pathname && url.search === here.search) {
      e.preventDefault();
      var el = scrollToHash(url.hash);
      stampState(false, url);
      if (el) {
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        attempt(function () { el.focus({ preventScroll: true }); });
      }
      return;
    }

    // Exactly where we already are. Let the browser reload; that is what
    // it does today and someone clicking the current page usually wants it.
    if (url.href === here.href) return;

    if (!isRoutableURL(url)) { STATS.declined += 1; return; }

    e.preventDefault();
    STATS.intercepted += 1;
    navigate(url.href, { popped: false });
  }

  /* ------------------------------------------------------------------ *
   * 20. BACK / FORWARD
   *
   * Direction is irrelevant: forward-through-history is still a restore.
   * Only a push is a "fresh" navigation that lands at the top.
   *
   * Registered in the CAPTURE phase. Dawn's facets.js also owns a window
   * popstate handler, and v1's defence against it depended on this file's
   * script tag preceding facets.js in document order. A capture-phase
   * listener on the target runs before every bubble-phase one regardless
   * of registration order, which turns an ordering assumption into a
   * guarantee. We do NOT stopImmediatePropagation even on our own entries:
   * killing other popstate handlers is a far larger blast radius than the
   * syncFacets resync below, which is enough.
   * ------------------------------------------------------------------ */
  function onPopState(e) {
    if (!enabled()) return;

    /* A popstate makes a pending PUSH navigation obsolete. v1 let the two
       race: the fragment and facet branches below returned without
       touching the in-flight fetch, and because commit() pushes only
       AFTER the fetch resolves, that navigation still held the current
       token and went on to pushState on top of the entry the user had
       just travelled back to. Net effect, reachable from the keyboard on
       every page via the skip link: Back moved the user backward for
       ~200ms and then threw them forward to the page they had abandoned,
       destroying the forward entry. To the user, Back is broken.

       Bumping seq is what actually cancels it — the abort alone would be
       enough for the fetch, but a commit already past its await boundary
       checks the token, not the controller. */
    if (inflight && !inflight.popped) {
      abortInflight('supersede');
      seq += 1;
      releasePending();
      log('popstate cancelled an in-flight navigation');
    }

    /* Capture the OUTGOING offset before currentEntryId moves. The browser
       has already flipped history.state to the destination entry, but with
       scrollRestoration:'manual' it has NOT scrolled, so currentY() is
       still where the user was. */
    recordScroll();
    persistOffsets();

    var url = new URL(root.location.href);
    var here = new URL(rendered.href);
    var st = e.state;

    // Do not steal Dawn's own facet history entries. They carry
    // searchParams but not sxRouter, and facets.js owns re-rendering them.
    if (url.pathname === here.pathname && st && typeof st.searchParams === 'string' && !st.sxRouter) {
      if (typeof st.sxId === 'number') currentEntryId = st.sxId;
      return;
    }

    // Fragment-only move within the same document: scroll, do not fetch.
    if (url.pathname === here.pathname && url.search === here.search) {
      if (st && typeof st.sxId === 'number') currentEntryId = st.sxId;
      if (url.hash) scrollToHash(url.hash);
      else {
        var y0 = (typeof currentEntryId === 'number' && typeof offsets[currentEntryId] === 'number')
          ? offsets[currentEntryId] : 0;
        restoreScroll(y0, seq);
      }
      return;
    }

    if (!isRoutableURL(url)) {
      // We cannot render it and the browser has already moved the address
      // bar, so the only honest recovery is a real load of where we are.
      root.location.reload();
      return;
    }

    var y = (st && typeof st.sxId === 'number' && typeof offsets[st.sxId] === 'number') ? offsets[st.sxId] : 0;

    // The entry may predate us (a hard load in the middle of the session
    // leaves entries with no sxId); stamp it so its offset is recorded
    // from here on. Either way currentEntryId ends up on the DESTINATION.
    if (!st || typeof st.sxId !== 'number') stampState(true, url);
    else currentEntryId = st.sxId;

    // Resync Dawn's facet statics NOW, synchronously, not after the commit.
    // Our capture-phase listener runs first, but navigate() is async, so
    // Dawn's handler runs on this same event with searchParamsPrev still
    // holding the previous page's query. Matching them here makes its
    // `!==` guard short-circuit instead of calling renderPage() against a
    // page with no #ProductGridContainer.
    syncFacets(url);

    navigate(url.href, { popped: true, restoreY: y });
  }

  /* ------------------------------------------------------------------ *
   * 21. BOOT
   *
   * Everything that reads or writes the DOM happens here, so the file is
   * indifferent to whether its <script> tag carries defer. Loaded early
   * without defer, it waits for DOMContentLoaded; loaded late, it runs
   * immediately. Nothing before this point has registered a listener or
   * mutated a global — which is what makes the disabled case a true
   * no-op rather than a smaller amount of work.
   * ------------------------------------------------------------------ */
  function refuse(reason) {
    root.sxRouter = { enabled: false, version: VERSION, reason: reason };
    log('refused to arm: ' + reason);
  }

  function boot() {
    main = doc.getElementById(MAIN_ID);
    if (!main) {
      // No swap target means no router. Say so plainly and leave the site
      // exactly as it was.
      refuse('no #' + MAIN_ID);
      return;
    }

    /* HARD capability gate on the reveal engine — see §9. Checked here
       rather than at module scope because a deferred spatialx-motion.js
       has not run yet when this file is parsed. If the theme is carrying
       the older motion asset (no mount/unmount, but it still hides every
       .sx-reveal behind body.sx-js), arming would render every swapped-in
       section permanently invisible with a clean console. Install
       nothing. */
    if (!motionAPI()) {
      refuse('no window.sxMotion.mount/unmount (old spatialx-motion.js)');
      return;
    }

    announcer = buildAnnouncer();
    toast = buildToast();
    injectStyles();
    guardDefine();

    // Seeded AFTER parse so every asset and stylesheet the parser already
    // ran is on record. Seeding in <head> would miss the ones inside
    // <main> and we would re-execute them on the first return visit.
    seedSrc();
    seedStyles();

    installScrollHandling();
    installMediaBus();

    doc.addEventListener('click', onClick, false);
    root.addEventListener('popstate', onPopState, true);   // capture: see §20

    ensureEntryId();   // stamp the landing entry before the user can navigate
    syncFacets(new URL(root.location.href));

    root.sxRouter = API;
    log('armed', VERSION);
  }

  var API = {
    enabled: true,
    version: VERSION,

    // For the two places in this theme that assign window.location.href
    // directly and therefore cannot be caught by link delegation:
    // #sx-roll-btn in layout/theme.liquid and onTickerClick() in
    // spatialx-radio.js. Both are <button>s outside <main>. Until they are
    // patched to call this, those two paths still kill the audio.
    navigate: function (href) {
      if (!enabled()) { try { root.location.assign(href); } catch (e) {} return; }
      navigate(href, { popped: false });
    },

    // Per-section AbortSignal for section scripts that need document- or
    // window-level listeners. See scopeFor() for why this exists.
    scope: scopeFor,

    /* Support levers. All of them survive a reload; none needs a deploy.
         sxRouter.disable()                  — off for this visitor
         sxRouter.enable()                   — back on, breaker cleared
         sxRouter.skipTemplates('collection')— hand one template to the browser
         sxRouter.skipTemplates('')          — clear that list
         ?sx_router=0 / ?sx_router=1         — the URL equivalents, and the
                                               only ones that work when
                                               sessionStorage is broken */
    disable: function () { sset(K_FLAG, 'off'); root.SX_ROUTER = false; },
    enable: function () { sset(K_FLAG, 'on'); sdel(K_BREAK); sdel(K_FAILS); root.SX_ROUTER = true; },
    skipTemplates: function (list) {
      var v = (list && list.join) ? list.join(',') : String(list || '');
      if (v) sset(K_SKIP, v); else sdel(K_SKIP);
      return skipTemplates();
    },

    /* Everything the first support call needs, from the console, without a
       repro: what we think is rendered, how many swaps this document has
       survived, whether the breaker is armed, and the last few things that
       happened. The log ring is bounded and holds only strings. */
    state: function () {
      return {
        version: VERSION,
        rendered: rendered.href,
        template: main ? main.getAttribute('data-template') : null,
        seq: seq,
        swaps: swaps,
        swapBudget: MAX_SWAPS,
        entryId: currentEntryId,
        inflight: inflight ? inflight.url.href : null,
        failures: failureCount(),
        broken: sget(K_BREAK) === 'off',
        storage: STORAGE_OK,
        skipTemplates: skipTemplates(),
        offsets: Object.keys(offsets).length,
        scripts: Object.keys(executedSrc).length,
        styles: Object.keys(loadedStyles).length,
        stats: {
          intercepted: STATS.intercepted,
          declined: STATS.declined,
          committed: STATS.committed,
          hardNavigations: STATS.hardNavigations,
          stayedPut: STATS.stayedPut,
          errors: STATS.errors
        },
        log: LOG.slice(-20)
      };
    }
  };

  // Same object under the name the handover uses for deep inspection.
  API.inspect = API.state;

  // Published immediately so anything loading alongside us can feature-
  // detect without caring about parse order; boot() re-publishes the same
  // object (or the disabled stub) once the DOM is available.
  root.sxRouter = API;

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
}());
